import { observer } from "mobx-react-lite";
import type { RefObject } from "react";

import type { ChatInputRef } from "./ChatInput";
import VibeChatActiveConversation from "./VibeChatActiveConversation";
import VibeChatEmptyState from "./VibeChatEmptyState";
import VibeChatLoadingShimmer from "./VibeChatLoadingShimmer";
import type { VibeChatStore } from "./stores";

export enum VibeChatBodyState {
  Active = "active",
  Empty = "empty",
  Loading = "loading",
}

export const getVibeChatBodyState = ({
  hasMessages,
  isLoading,
  isLoadingChat,
}: {
  hasMessages: boolean;
  isLoading: boolean;
  isLoadingChat: boolean;
}) => {
  if (isLoadingChat) {
    return VibeChatBodyState.Loading;
  }

  if (!hasMessages && !isLoading) {
    return VibeChatBodyState.Empty;
  }

  return VibeChatBodyState.Active;
};

interface VibeChatBodyContentProps {
  bodyState: VibeChatBodyState;
  chatStore: VibeChatStore;
  chatInputRef: RefObject<ChatInputRef>;
  inputValue: string;
  setInputValue: (value: string) => void;
  onSubmit: (message: string) => void;
  handleStop: () => void;
  handleRetry: (messageIndex?: number) => void;
}

export const VibeChatBodyContent = observer(
  ({
    bodyState,
    chatStore,
    chatInputRef,
    inputValue,
    setInputValue,
    onSubmit,
    handleStop,
    handleRetry,
  }: VibeChatBodyContentProps) => {
    switch (bodyState) {
      case VibeChatBodyState.Loading:
        return <VibeChatLoadingShimmer />;
      case VibeChatBodyState.Empty:
        return (
          <VibeChatEmptyState
            chatInputRef={chatInputRef}
            inputValue={inputValue}
            setInputValue={setInputValue}
            onSubmit={onSubmit}
            handleStop={handleStop}
            isLoading={chatStore.isLoading}
          />
        );
      case VibeChatBodyState.Active:
      default:
        return <VibeChatActiveConversation chatStore={chatStore} handleRetry={handleRetry} />;
    }
  },
);
